'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { getProductUrl, type ProductHit } from '@libs/algolia';
import { getBuyItAgainGuest, getProductsByIds } from '@libs/api-proxy-client';
import type { BuyItAgainGuestApiResponse, BuyItAgainGuestPlacement } from '@libs/common-types';
import { getLocale, getVisitorId, logger } from '@libs/core';
const OPTEX_SEARCH_BIA_GUEST_COUNT = 20;
const OPTEX_SEARCH_BIA_GUEST_CALLER = 'web:searchbar:bia-guest';
export type OptexSearchBiaGuestProduct = {
  sku: string;
  recRank: number;
  name: string;
  imageUrl: string;
  href: string;
};
export function getOptexSearchBiaGuestPlacement(pathname: string): BuyItAgainGuestPlacement {
  return /(?:^|\/)cart\/?$/.test(pathname) ? 'incart' : 'homepage';
}
function mapOptexSearchBiaGuestProducts(recommendations: BuyItAgainGuestApiResponse, hits: ProductHit[]): OptexSearchBiaGuestProduct[] {
  const optexHitsBySku = new Map(hits.filter(Boolean).map(hit => [hit.objectID, hit]));
  const optexProducts = recommendations.flatMap(recommendation => {
    const optexHit = optexHitsBySku.get(recommendation.sku_nbr);
    const optexImageUrl = optexHit?.images?.small ?? optexHit?.images?.large;
    if (!optexHit?.name || !optexHit.masterProductID || !optexImageUrl) return [];
    return [{
      sku: recommendation.sku_nbr,
      recRank: recommendation.rec_rank,
      name: optexHit.name,
      imageUrl: optexImageUrl,
      href: getProductUrl(optexHit)
    }];
  });
  if (optexProducts.length < recommendations.length) {
    logger.warn(`[OPTEX SEARCH BIA GUEST][Warn]: Resolved ${optexProducts.length} of ${recommendations.length} recommendations; rendering resolved products`);
  }
  return optexProducts;
}
async function fetchOptexSearchBiaGuestRecommendations(placement: BuyItAgainGuestPlacement): Promise<BuyItAgainGuestApiResponse> {
  try {
    return await getBuyItAgainGuest({
      placement,
      count: OPTEX_SEARCH_BIA_GUEST_COUNT
    });
  } catch (error) {
    logger.error('[OPTEX SEARCH BIA GUEST][Error]: Recommendation request failed', error);
    return [];
  }
}
async function fetchOptexSearchBiaGuestProducts(recommendations: BuyItAgainGuestApiResponse): Promise<OptexSearchBiaGuestProduct[]> {
  try {
    const hits = await getProductsByIds({
      ids: recommendations.map(recommendation => recommendation.sku_nbr),
      caller: OPTEX_SEARCH_BIA_GUEST_CALLER
    });
    return mapOptexSearchBiaGuestProducts(recommendations, hits);
  } catch (error) {
    logger.error('[OPTEX SEARCH BIA GUEST][Error]: Product lookup failed', error);
    return [];
  }
}
export function useOptexSearchBiaGuest(isOptexSearchBiaGuestEnabled) {
  const $ = _c(14);
  const optexPathname = usePathname() ?? "";
  let t0;
  if ($[0] !== optexPathname) {
    t0 = getOptexSearchBiaGuestPlacement(optexPathname);
    $[0] = optexPathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const optexPlacement = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getLocale();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const optexLocale = t1;
  const [optexVisitorId, setOptexVisitorId] = useState("");
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [optexProducts, setOptexProducts] = useState(t2);
  let t3;
  let t4;
  if ($[4] !== isOptexSearchBiaGuestEnabled) {
    t3 = () => {
      setOptexVisitorId(isOptexSearchBiaGuestEnabled ? getVisitorId() : "");
    };
    t4 = [isOptexSearchBiaGuestEnabled];
    $[4] = isOptexSearchBiaGuestEnabled;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isOptexSearchBiaGuestEnabled || $[8] !== optexPlacement || $[9] !== optexVisitorId) {
    t5 = () => {
      if (!isOptexSearchBiaGuestEnabled || !optexVisitorId) {
        setOptexProducts([]);
        return;
      }
      let isCancelled = false;
      const fetchOptexSearchBiaGuest = async function fetchOptexSearchBiaGuest() {
        const recommendations = await fetchOptexSearchBiaGuestRecommendations(optexPlacement);
        if (isCancelled) {
          return;
        }
        if (recommendations.length === 0) {
          setOptexProducts([]);
          return;
        }
        const products = await fetchOptexSearchBiaGuestProducts(recommendations);
        if (!isCancelled) {
          setOptexProducts(products);
        }
      };
      fetchOptexSearchBiaGuest();
      return () => {
        isCancelled = true;
      };
    };
    t6 = [isOptexSearchBiaGuestEnabled, optexLocale, optexPlacement, optexVisitorId];
    $[7] = isOptexSearchBiaGuestEnabled;
    $[8] = optexPlacement;
    $[9] = optexVisitorId;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== optexProducts) {
    t7 = {
      optexProducts
    };
    $[12] = optexProducts;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}